<template>
  <div class="wizzard__form">
    <UIList
      :get-key="item => item.key"
      :columns
      :items
      class="private-fund-multiple-flows"
      @remove="handleRemove"
    >
      <template #account="{ item, hovered }">
        <UIAssetName
          :name="entityLabel(item.entity)"
          class="line-clamp-2"
        />
        <UIAddRowButton
          v-if="hovered"
          v-tooltip.top="`Add row for ${entityLabel(item.entity)}`"
          @click="handleAddRow(item.entity, item.index)"
        />
      </template>
      <template #date="{ item }">
        <component
          :is="item.transaction.getFormDate()"
          :instance="item.transaction"
          :size
        />
      </template>
      <template #type="{ item }">
        <component
          :is="item.transaction.getFormType()"
          :instance="item.transaction"
          :size
          :restrictions
        />
      </template>
      <template #amount="{ item }">
        <component
          :is="item.transaction.getFormAmount2()"
          :instance="item.transaction"
          :size
          :placeholder="`Amount in ${currency}`"
        />
      </template>
    </UIList>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, onMounted, onUnmounted, ref } from 'vue'
import { cloneDeep, orderBy } from 'lodash'

import { InputSize, Transaction, TransactionClass } from '@types'

import { WizardEntity } from '../../utils/types'

import {
  NOTIFICATION_DELAY,
  TRANSACTION_ENTRY_FIELD,
  TRANSACTION_ENTRY_FIELD_LABEL,
  TRANSACTION_FIELD,
  TRANSACTION_FIELD_LABEL,
} from '@/const'

import { getCurrentDate } from '@/helpers/dates'
import { getEntityAccountName } from '../../utils/helpers'
import { generateId } from '@/entities/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { useRepositoriesStore } from '@/store/repositories/index'

import { useNotifications } from '@/plugins/notification'

import { UIAssetName, UIList } from '@ui/core'
import { UIAddRowButton } from '@ui/buttons'

type Item = {
  index: number,
  entity: WizardEntity, 
  transaction: TransactionClass 
}

type Props = {
  flowType: 'contributions' | 'distributions'
  entities: WizardEntity[]
  restrictions: string[]
}

const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()
const repositoriesStore = useRepositoriesStore()

const { warn } = useNotifications()

const size = inject<InputSize>('size')
const currency = inject<ComputedRef<string>>('currency')
const defaultDate = inject<ComputedRef<string | undefined>>('defaultDate')

const defaultType = ref(props.restrictions[0])

const columns = computed(() => [
  {
    name: 'account',
    caption: 'Investor',
  },
  {
    name: 'date',
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DATE],
  },
  {
    name: 'type',
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.TYPE],
  },
  {
    name: 'amount',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT],
  },
])

const items = computed(() =>
  props.entities.reduce((acc, entity) => {
    // New-account entities have no account_id until save time, but their flows
    // are keyed off the entity itself — only skip existing-account entities
    // whose account hasn't resolved yet.
    const accountId = entity.transaction?.entry1().value?.account_id
    if (entity.useExisting && !accountId) return acc
    acc.push(...entity[props.flowType].map((transaction, index) => ({
      key: generateId(),
      index: index,
      entity,
      transaction,
    })))
    return acc
  }, [] as Item[]),
)

const repositoryId = computed(() => repositoriesStore.getCurrentId)

const defaultData = computed<Transaction>(() => ({
  id: '',
  date: defaultDate?.value || getCurrentDate({}),
  type: defaultType.value,
  entries: [
    {
      account_id: '',
      amount: null,
      asset_id: '',
    },
    {
      account_id: '',
      amount: null,
      asset_id: '',
    },
  ],
  repository_id: `${repositoryId.value}`,
  description: '',
}))

const addTransaction = (entity: WizardEntity, index = 0) => {
  const transaction = new TransactionClass(cloneDeep(defaultData.value), false, false)
  entity[props.flowType].splice(index, 0, transaction)
} 

const entityLabel = (entity: WizardEntity) => 
  getEntityAccountName(entity, id => assetsBunchStore.getElementById(id))

const handleAddRow = (entity: WizardEntity, index: number) => {
  addTransaction(entity, index + 1)
}

const handleRemove = (_: number, item: Item) => {
  if (item.entity[props.flowType].length === 1) {
    item.entity[props.flowType][0].pasteDate(defaultDate?.value || getCurrentDate({}))
    item.entity[props.flowType][0].pasteType(defaultType.value)
    item.entity[props.flowType][0].pasteAmount2('')
    return
  }
  item.entity[props.flowType].splice(item.index, 1)
}

onMounted(() => {
  props.entities.forEach(entity => {
    if (entity[props.flowType].length) return
    addTransaction(entity)
  })
})

const checkEntitiesDates = () => {
  props.entities.forEach(entity => {
    const erliestDate = orderBy(entity[props.flowType], item => item.field(TRANSACTION_FIELD.DATE).value, ['asc'])?.at(0)?.field(TRANSACTION_FIELD.DATE).value
    const currentDate = entity.transaction?.field(TRANSACTION_FIELD.DATE).value
    if (entity.transaction && erliestDate && erliestDate !== currentDate) {
      entity.transaction.field(TRANSACTION_FIELD.DATE).value = erliestDate
      warn({ message: 'The investment date has been updated to the earliest date of the contributions' }, NOTIFICATION_DELAY)
    }
  })
}

onUnmounted(() => {
  props.entities.forEach(entity => {
    entity[props.flowType] = entity[props.flowType]
      .filter(transaction => transaction.entry2().value?.amount)
  })
  if (props.flowType === 'contributions') {
    checkEntitiesDates()
  }
})
</script>


<style>
.private-fund-multiple-flows {
  @apply grid-cols-[minmax(120px,1fr)_145px_minmax(150px,1fr)_112px_min-content];
  @apply sm:grid-cols-[1fr_120px_1fr_97px_min-content];

  .ui-add-row-button {
    @apply top-full!;
  }
}
</style>
